import { Fragment } from 'react';
import styled from 'styled-components';
import { Content, Stack, Strong } from '@kinesis/bungle';
import { DisclosureGpcDetailPage } from '@/types';
import { renderUnit, renderValue } from '@/data/units';
import {
  gpcLeafDescription,
  gpcLeafRef,
  gpcRefLineRef,
  renderGpcRefLine,
  renderGpcSector,
  renderNotationKey,
} from '@/data/gpc';

// Five columns: GPC ref · scope · GHG emissions source · notation · value.
const COLUMNS = 'auto auto minmax(0, 1fr) auto auto';

// Bespoke table chrome for this page. GPC Detail and GPC Summary are
// independent table variants, each owning its own grid — deliberately NOT a
// shared component (this is a three-level sector→line→leaf form; Summary is a
// two-level grouped header). Styling matches bungle's nursery `Table`: 1px
// gridlines (the grid's `gap` showing through a border-coloured backing) inside
// a 1px frame, a `#fafafa` bold header band (`$heading`) for the column header,
// sector and sub-sector aggregation rows, 12px cell padding and a 32px row
// height. The nursery `Table` is column-config and can't express the spanned
// heading rows, so the grid is hand-rolled (like the legacy `gpc-detail-table`).
const Table = styled<any>('div')(
  ({ theme }: any) => ({
    display: 'grid',
    gap: 1,
    backgroundColor: theme.borderColor,
    border: `1px solid ${theme.borderColor}`,
    gridAutoRows: 32,
    fontSize: theme.fontSize.medium,
    width: '100%',
  }),
  ({ $columns }: any) => ({ gridTemplateColumns: $columns }),
);

// One grid cell. `$heading` is the bold `#fafafa` band (column header, sector +
// sub-sector aggregation rows, total); `$align` right-aligns the value column;
// `$indent` left-pads a leaf's ref under its sub-sector; `$span` widens a cell
// across N columns (the sector/total rows). Single line, ellipsised; the 32px
// line-height vertically centres against the row.
const Cell = styled<any>('div')(
  ({ $heading, $align, $indent, $span }: any) => ({
    backgroundColor: $heading ? '#fafafa' : 'white',
    fontWeight: $heading ? 'bold' : 'normal',
    textAlign: $align ?? 'left',
    lineHeight: '32px',
    padding: '0 12px',
    paddingLeft: $indent ? 28 : undefined,
    gridColumn: $span ? `span ${$span}` : undefined,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

// Renders the GPC Detail page — the full standard form: every reference line
// under its sector, in fixed GPC order, populated or empty. Three levels:
// sector → sub-sector (`.X`) → leaf (`.Y`). The aggregation rows (sector,
// sub-sector) carry the rolled total; only leaves carry a scope + value. An
// empty line carries its `notation` (`NE`) on the sub-sector row — never on a
// leaf, never inferred (matches the server's (iii) shape). The FE derives
// every display string from the structured payload: the ref string (`I.1.1`),
// the names, and the per-(sector, scope) leaf description. A blank value
// renders "—", never 0. Store-less; the page owns the RTK call.
const DisclosureGpcDetail = (props: DisclosureGpcDetailProps) => {
  const { pageData } = props;
  const { unit, sectors, total } = pageData;
  const value = (v: number | undefined) =>
    v === undefined ? '—' : renderValue(v, unit);
  return (
    <Stack space='medium'>
      <Strong>GHG emissions detail ({renderUnit(unit)})</Strong>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Table $columns={COLUMNS}>
          <Cell $heading>GPC ref</Cell>
          <Cell $heading>Scope</Cell>
          <Cell $heading>GHG emissions source</Cell>
          <Cell $heading>Notation</Cell>
          <Cell $heading $align='right'>
            {renderUnit(unit)}
          </Cell>
          {sectors.map((sector) => (
            <Fragment key={sector.sector}>
              {/* Sector heading row: name spans ref→notation, total at right. */}
              <Cell $heading $span={4}>
                {renderGpcSector(sector.sector)}
              </Cell>
              <Cell $heading $align='right'>
                {value(sector.total)}
              </Cell>
              {sector.subSectors.map((sub) => (
                <Fragment key={sub.subSector}>
                  {/* Sub-sector aggregation row. */}
                  <Cell $heading>
                    {gpcRefLineRef(sector.sector, sub.subSector)}
                  </Cell>
                  <Cell $heading aria-hidden='true' />
                  <Cell $heading>{renderGpcRefLine(sub.subSector)}</Cell>
                  <Cell $heading>
                    {sub.notation ? renderNotationKey(sub.notation) : ''}
                  </Cell>
                  <Cell $heading $align='right'>
                    {value(sub.total)}
                  </Cell>
                  {/* Leaf rows: one per reached scope. */}
                  {sub.leaves.map((leaf) => (
                    <Fragment key={leaf.scope}>
                      <Cell $indent>
                        {gpcLeafRef(sector.sector, sub.subSector, leaf.scope)}
                      </Cell>
                      <Cell>{leaf.scope}</Cell>
                      <Cell>
                        {gpcLeafDescription(sector.sector, leaf.scope)}
                      </Cell>
                      <Cell aria-hidden='true' />
                      <Cell $align='right'>
                        {renderValue(leaf.value, unit)}
                      </Cell>
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
          <Cell $heading $span={4}>
            Total
          </Cell>
          <Cell $heading $align='right'>
            {value(total)}
          </Cell>
        </Table>
      </Content>
    </Stack>
  );
};

export type DisclosureGpcDetailProps = {
  pageData: DisclosureGpcDetailPage;
};

export { DisclosureGpcDetail };
